import { CartUpdateEvent } from '@theme/events';

const PFAND_VARIANT_ID = 58750663754073;
const nativeFetch = window.fetch.bind(window);

let syncTimer = null;
let syncInFlight = null;
let syncRequestedWhileRunning = false;
let pendingMutations = 0;
let syncFailed = false;

function publishSyncState() {
  window.cartSyncState = { pending: Boolean(pendingMutations || syncTimer || syncInFlight), failed: syncFailed };
  document.dispatchEvent(new CustomEvent('cart:sync-state'));
}

function sectionRequest() {
  const sections = [...new Set([...document.querySelectorAll('cart-items-component')]
    .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
  return { sections: sections.join(','), sections_url: window.location.pathname };
}

async function fetchCart() {
  const response = await nativeFetch('/cart.js', {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Cart fetch failed: ${response.status}`);
  }

  return response.json();
}

export function isCartLineMutation(input) {
  let rawUrl = '';

  if (typeof input === 'string') {
    rawUrl = input;
  } else if (typeof Request !== 'undefined' && input instanceof Request) {
    rawUrl = input.url;
  } else if (input && typeof input.url === 'string') {
    rawUrl = input.url;
  }

  if (!rawUrl) return false;

  let url;
  try { url = new URL(rawUrl, window.location.origin); } catch { return false; }
  if (url.origin !== window.location.origin) return false;
  const pathname = url.pathname.replace(/\/+$/, '').replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/cart\/)/i, '');

  return (
    pathname === '/cart/add' ||
    pathname === '/cart/add.js' ||
    pathname === '/cart/change' ||
    pathname === '/cart/change.js' ||
    pathname === '/cart/update' ||
    pathname === '/cart/update.js' ||
    pathname === '/cart/clear' ||
    pathname === '/cart/clear.js'
  );
}

async function syncPfandOnce() {
  const cart = await fetchCart();
  let pfandQty = 0;

  for (const item of cart.items) {
    if (Number(item.variant_id) === Number(PFAND_VARIANT_ID)) continue;

    let isTray = false;
    if (item.properties && item.properties.is_tray === 'true') isTray = true;
    if (item.product_type && String(item.product_type).toLowerCase() === 'tray') isTray = true;
    pfandQty += Number(item.quantity) * (isTray ? 8 : 1);
  }

  const existingPfand = cart.items.find((item) => Number(item.variant_id) === Number(PFAND_VARIANT_ID));
  const currentQty = existingPfand ? Number(existingPfand.quantity) : 0;

  if (pfandQty === currentQty) return cart;

  let updatedCart;
  if (existingPfand) {
    const body = JSON.stringify({ id: existingPfand.key, quantity: pfandQty, ...sectionRequest() });
    const response = await nativeFetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    });

    if (!response.ok) throw new Error(`Pfand change failed: ${response.status}`);
    updatedCart = await response.json();
    if (!Array.isArray(updatedCart.items) || updatedCart.errors) throw new Error('Invalid Pfand cart response');
  } else if (pfandQty > 0) {
    const response = await nativeFetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(PFAND_VARIANT_ID), quantity: Number(pfandQty) }], ...sectionRequest() }),
    });

    if (!response.ok) throw new Error(`Pfand add failed: ${response.status}`);
    const added = await response.json();
    updatedCart = await fetchCart();
    updatedCart.sections = added.sections;
  }

  if (!updatedCart) return cart;
  const confirmedQty = updatedCart.items
    .filter((item) => Number(item.variant_id) === PFAND_VARIANT_ID)
    .reduce((total, item) => total + Number(item.quantity), 0);
  if (confirmedQty !== pfandQty) throw new Error('Pfand quantity was not confirmed by Shopify');
  document.dispatchEvent(
    new CartUpdateEvent(updatedCart, 'cart-sync', {
      source: 'cart-sync',
      itemCount: updatedCart.item_count,
      sections: updatedCart.sections,
    })
  );

  return updatedCart;
}

async function runPfandSync() {
  if (syncInFlight) return syncInFlight;
  syncInFlight = (async () => {
    let cart;
    do {
      syncRequestedWhileRunning = false;
      cart = await syncPfandOnce();
    } while (syncRequestedWhileRunning);
    syncFailed = false;
    return cart;
  })();
  publishSyncState();
  try {
    return await syncInFlight;
  } catch (error) {
    syncFailed = true;
    throw error;
  } finally {
    syncInFlight = null;
    publishSyncState();
  }
}

function schedulePfandSync() {
  if (syncInFlight) {
    syncRequestedWhileRunning = true;
    return;
  }

  if (syncTimer) clearTimeout(syncTimer);

  syncTimer = setTimeout(() => {
    syncTimer = null;
    runPfandSync().catch((error) => {
      console.error('syncPfand error', error);
    });
  }, 0);
  publishSyncState();
}

async function syncPfand() {
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }

  return runPfandSync();
}

(function attachFetchHook() {
  window.fetch = async function (...args) {
    const mutation = isCartLineMutation(args[0]);
    if (mutation) {
      pendingMutations++;
      publishSyncState();
    }
    try {
      return await nativeFetch(...args);
    } finally {
      if (mutation) {
        pendingMutations--;
        // Re-read even after errors: a lost response does not prove the write failed.
        schedulePfandSync();
        publishSyncState();
      }
    }
  };
})();

function runInitialSync() {
  syncPfand().catch((error) => {
    console.error('Initial Pfand sync failed.', error);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runInitialSync, { once: true });
} else {
  runInitialSync();
}

window.syncPfand = syncPfand;

export { syncPfand };
